import { Annotation } from '@nutrient-sdk/viewer';

import { useMutation, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  getTask,
  Task,
  createUploadTaskUrl as CREATE_UPLOADTASK_URL,
  // updateTask as UPDATE_TASK,
  getTask as GET_TASK,
} from '@admiin-com/ds-graphql';
import { useCurrentEntityId } from '../../../hooks/useSelectedEntity/useSelectedEntity';
import { uploadPdfToSignedUrl } from '../../../helpers/signature';
import { useSnackbar } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import { useUpdateTask } from '../../../hooks/useUpdateTask/useUpdateTask';
import useTasksFromurlByEntityId from '../useTasksFromUrl/useSearchTasks';
import { useEntityId } from '../useEntityContext/useEntityContext';
import useTaskSearchFilters from '../useTaskSearchFilters/useTaskSearchFilters';

export const useSignFunc = (props: { task: Task }) => {
  const [getTaskFunc] = useLazyQuery(gql(getTask));
  const [createUploadTaskUrl] = useMutation(gql(CREATE_UPLOADTASK_URL));
  const [updateTaskFunc] = useUpdateTask();
  const userId = useCurrentEntityId();
  const showSnackbar = useSnackbar();
  const { t } = useTranslation();
  const entityId = useEntityId();
  const { filters } = useTaskSearchFilters();
  const { refetch } = useTasksFromurlByEntityId({
    entityId,
    ...filters,
    disableAutoFetch: true,
  });

  const updateAnnotations = async (annoations: string, pdf: any) => {
    try {
      const taskData = await getTaskFunc({
        variables: { id: props.task.id, entityId: props.task.entityId },
        fetchPolicy: 'no-cache',
      });
      const task = taskData?.data?.getTask;
      const preSignedAnnotations = JSON.parse(task.annotations)?.annotations;

      const newAnnotations = JSON.parse(annoations)?.annotations;
      const postSignedAnnotations = preSignedAnnotations.map(
        (annotation: any) => {
          const userAnnotation = newAnnotations.find(
            (newAnnotation: Annotation) =>
              newAnnotation?.customData?.id === annotation?.customData?.id
          );
          return userAnnotation ?? annotation;
        }
      );

      const postSignedAnnotationsString = JSON.stringify({
        ...JSON.parse(task.annotations),
        annotations: postSignedAnnotations,
        attachments: {
          ...JSON.parse(task.annotations).attachments,
          ...(JSON.parse(annoations).attachments ?? {}),
        },
      });

      const input: any = {
        id: props.task.id,
        entityId: props.task.entityId,
        annotations: postSignedAnnotationsString,
      };

      const isFinalSigner = postSignedAnnotations.every(
        (annotation: Annotation) =>
          annotation?.customData?.status !== 'PENDING' ||
          annotation?.customData?.signerId === userId
      );

      if (isFinalSigner) {
        try {
          const uploadUrlData = await createUploadTaskUrl({
            variables: {
              input: {
                id: props.task.id,
                entityId: props.task.entityId,
              },
            },
          });

          const uploadUrl = uploadUrlData.data.createUploadTaskUrl.url;

          await uploadPdfToSignedUrl(pdf, uploadUrl);
        } catch (error: any) {
          if (error.message !== 'UNAUTHORISED_NOT_FINAL_SIGNER') {
            throw error;
          }
        }
      }
      await updateTaskFunc({
        variables: {
          input,
        },
        refetchQueries: [
          {
            query: gql(GET_TASK),
            variables: {
              id: task?.id,
              entityId: task?.entityId,
            },
            fetchPolicy: 'network-only',
          },
        ],
        awaitRefetchQueries: true,
      });

      await refetch();
      showSnackbar({
        message: t('Signed successfully', { ns: 'taskbox' }),
        severity: 'success',
        horizontal: 'right',
        vertical: 'bottom',
      });
    } catch (e: any) {
      showSnackbar({
        message: t(e.message, { ns: 'payment' }),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
      throw e;
    }
  };

  return updateAnnotations;
};
